import React, { useState, useEffect } from "react";
import Card from "../../components/ui/Card";
import InputWithIcon from "../../components/ui/InputWithIcon";
import Button from "../../components/ui/Button";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/api";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

const OpeningBalanceAdd = ({ setView }) => {
  const { user } = useAuth();

  // ✅ Set default today date in YYYY-MM-DD format
  const today = new Date().toISOString().split("T")[0];

  const [formData, setFormData] = useState({
    vendor: null,
    invNo: "",
    invDate: today, // 👈 default today
    amount: "",
    notes: "",
  });

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ Fetch vendors from API
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        setLoading(true);
        const username = user?.username || "admin"; // fallback
        const response = await api.GetPartylist(username);
        setVendors(response.Table || []);
      } catch (err) {
        console.error("Failed to fetch vendors:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchVendors();
  }, [user?.username]);

  // ✅ Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.vendor) {
      alert("Please select a vendor.");
      return;
    }

    try {
      const payload = {
        SqlAction: "insert",
        Cpid: 1, // ✅ set your company id here
        Vid: formData.vendor.Vid,
        Vcode: formData.vendor.Vcode,
        Vname: formData.vendor.Vname,
        Invno: formData.invNo,
        Invdate: new Date(formData.invDate).toISOString(), // 👈 always ISO
        NetAmt: Number(formData.amount),
        Notes: formData.notes,
      };

      console.log("Submitting payload:", payload);

      const response = await api.SaveOpenningBalance(payload);
      console.log("API Response:", response);

      const table =
        response?.SaveOpenningBalanceAPIjs?.Table ||
        response?.Table ||
        [];

      if (table.length > 0) {
        const firstRow = table[0];

        if (firstRow.Error) {
          // ✅ Backend returned an error
          alert(
            `Save failed: Invoice No "${firstRow.Invno}" already exists for this vendor.`
          );
        } else if (firstRow.Column1) {
          // ✅ Success
          alert(`Saved successfully!`);
          setView("openingbalancelist");
        } else {
          alert("Save failed. Unexpected response from server.");
        }
      } else {
        alert("Save failed. No response from server.");
      }
    } catch (err) {
      console.error("Save failed:", err);
      alert("Error saving opening balance.");
    }
  };

  return (
    <div className="w-full px-0 sm:max-w-4xl sm:mx-0 sm:pr-4">
      <Card title="Opening Balance" className="w-full px-0 sm:px-6">
        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            {/* Vendor with Search */}
            <div>
              <label className="block mb-1 font-semibold text-gray-700">
                Party*
              </label>
              <Autocomplete
                options={vendors}
                getOptionLabel={(option) =>
                  `${option.Vname} (${option.Vcode}) - ${option.VType}`
                }
                loading={loading}
                value={formData.vendor}
                onChange={(e, newValue) =>
                  setFormData((prev) => ({ ...prev, vendor: newValue }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search vendor by name"
                    required
                  />
                )}
              />
            </div>

            {/* Invoice No */}
            <div>
              <label
                htmlFor="invNo"
                className="block mb-1 font-semibold text-gray-700"
              >
                Invoice No*
              </label>
              <input
                type="text"
                id="invNo"
                value={formData.invNo}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, invNo: e.target.value }))
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter Invoice No"
                required
              />
            </div>


            {/* Invoice Date */}
            <div>
              <label className="block mb-1 font-semibold text-gray-700">
                Invoice Date*
              </label>
              <input
                type="date"
                id="invDate"
                value={formData.invDate}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, invDate: e.target.value }))
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Amount */}
            <div>
              <label className="block mb-1 font-semibold text-gray-700">
                Amount*
              </label>
              <input
                type="number"
                id="amount"
                value={formData.amount}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, amount: e.target.value }))
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter amount"
                required
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block mb-1 font-semibold text-gray-700">
              Notes
            </label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter notes"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-4 pt-6 mt-8 border-t">
              <Button
              type="submit"
              className="px-3 py-1 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600"
            >
              Submit
            </Button>

            <Button
              type="button"
              onClick={() => setView("openingbalancelist")}
              className="px-3 py-1 text-sm font-medium text-white bg-gray-500 rounded hover:bg-gray-600"
            >
              Close
            </Button>
            
          </div>
        </form>
      </Card>
    </div>
  );
};

export default OpeningBalanceAdd;
